import type { CampaignUser } from "../../../../types/campaign";
import { getCurrentPhase } from "../../../utils";
import PATHS from "../../../constants/routePaths";
import type { PollerResolveData } from "../../../../types/poller";
import { buildContextStore } from "../../../stores/buildContext";
import { get } from "svelte/store";

export const StatusReason = {
  handler: (statusReason: string | null, actingFunction: (action: string) => void) => {
    let action: string;

    switch (statusReason) {
      case "supplier-rate-limited":
      case "rate-limited":
        action = PATHS.TOO_MUCH_DEMAND;
        break;
      case "stock-out":
      case "no-quota":
        action = PATHS.STOCK_OUT;
        break;
      case "no-suppliers":
      case "no-availability":
      case "unknown":
        action = PATHS.NO_SUPPORT;
        break;
      case "unlucky":
        action = PATHS.UNLUCKY;
        break;
      case "link-used":
        action = PATHS.LINK_USED;
        break;
      case "campaign-over":
        action = PATHS.CAMPAIGN_OVER;
        break;
      case "bot-detected":
        action = PATHS.BOT_DETECTED;
        break;
      case "invalid-channel":
        action = PATHS.INVALID_CHANNEL;
        break;
      case "invalid-entry":
        action = PATHS.INVALID_ENTRY;
        break;
      case "survey-invalid":
        get(buildContextStore).statusHandler.resetSurvey();
        action = PATHS.SURVEY;
        break;
      default:
        action = PATHS.CANCELLED;
    }

    actingFunction(action);
  }
};

const rejectionMap = {
  [PATHS.TOO_MUCH_DEMAND]: "Too much demand",
  [PATHS.STOCK_OUT]: "Out of stock",
  [PATHS.NO_SUPPORT]: "No support",
  [PATHS.UNLUCKY]: "Unlucky",
  [PATHS.LINK_USED]: "Link used",
  [PATHS.CAMPAIGN_OVER]: "Campaign over",
  [PATHS.BOT_DETECTED]: "Bot detected",
  [PATHS.INVALID_CHANNEL]: "Invalid channel",
  [PATHS.INVALID_ENTRY]: "Invalid entry",
  [PATHS.CANCELLED]: "Cancelled",
  [PATHS.SURVEY]: "Invalid survey"
};

class ErrorWithOptionalCampaignUser extends Error {
  campaignUser?: CampaignUser;

  constructor(message: string, campaignUser?: CampaignUser) {
    super(message);
    this.campaignUser = campaignUser;
  }
}

export const StatusRejection = {
  action: (reject: (e: unknown) => void, campaignUser?: CampaignUser) => (statusReasonId: string) => {
    const rejectionMessage = rejectionMap[statusReasonId] || rejectionMap.default;
    reject(new ErrorWithOptionalCampaignUser(`Workflow reached a bad status: ${rejectionMessage}`, campaignUser));
  }
};

export const defaultHandlerBuilder = (resolve: (phase: PollerResolveData) => void, reject: (e: unknown) => void) => {
  return  (campaignUser: CampaignUser) => {
    const { current_status: currentStatus, metadata, status_reason: statusReason } = campaignUser;
    const currentPhase = getCurrentPhase(metadata);

    if (currentStatus === "finished") {
      const nextStepPhases = ["thank-you", "thank-you-allow-repeat", "lucky-dip-phase", "duplicate-order"];

      if (nextStepPhases.includes(currentPhase) || currentPhase.startsWith("complete-order-")) {
        resolve({ currentPhase, campaignUser });
      } else {
        StatusReason.handler(statusReason, StatusRejection.action(reject, campaignUser));
      }
      return true;
    } else if (currentStatus === "fatal-error") {
      reject(new Error("Fatal workflow error"));
      return true;
    } 

    return false;
  };
};

// TODO: no support for the `waitForNextFinishedPhase` function yet, used for menus on the standard site
